import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

const ModalTest = () => {
  const [showModal, setShowModal] = useState(false);
  const [testResults, setTestResults] = useState<any>({});
  const [browserInfo, setBrowserInfo] = useState<any>({});

  useEffect(() => {
    // Detect browser and extensions
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const info: any = {
        userAgent,
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        language: navigator.language,
        platform: navigator.platform,
        vendor: navigator.vendor,
      };

      // Check for common ad blockers
      info.possibleAdBlocker = false;
      
      // Test 1: Check if common ad domains are blocked
      const testAdBlocker = () => {
        const testDiv = document.createElement('div');
        testDiv.innerHTML = '&nbsp;';
        testDiv.className = 'adsbox pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad text_ads text-ads text-ad-links ad-text adSense adBlock adsense';
        testDiv.style.position = 'absolute';
        testDiv.style.left = '-9999px';
        document.body.appendChild(testDiv);
        
        setTimeout(() => {
          if (testDiv.offsetHeight === 0) {
            info.possibleAdBlocker = true;
          }
          document.body.removeChild(testDiv);
          setBrowserInfo(info);
        }, 100);
      };

      testAdBlocker();
    };

    detectBrowser();
  }, []);

  const runTests = () => {
    const results: any = {};

    // Test 1: Check if Dialog portal container exists
    results.portalExists = document.querySelector('[data-radix-portal]') !== null;

    // Test 2: Check if modal is in DOM when open
    results.modalInDOM = document.querySelector('[role="dialog"]') !== null;

    // Test 3: Check z-index stacking
    const allElements = document.querySelectorAll('*');
    let highestZIndex = 0;
    allElements.forEach(el => {
      const zIndex = window.getComputedStyle(el).zIndex;
      if (zIndex !== 'auto') {
        const num = parseInt(zIndex);
        if (num > highestZIndex) highestZIndex = num;
      }
    });
    results.highestZIndex = highestZIndex;

    // Test 4: Check for overlay
    results.overlayExists = document.querySelector('[data-radix-dialog-overlay]') !== null;

    // Test 5: Check localStorage access
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      results.localStorageWorks = true;
    } catch {
      results.localStorageWorks = false;
    }

    // Test 6: Check for popup blocker
    const testPopup = window.open('', '', 'width=1,height=1');
    if (testPopup) {
      testPopup.close();
      results.popupBlockerActive = false;
    } else {
      results.popupBlockerActive = true;
    }

    // Test 7: Check DOM mutation
    results.domMutationAllowed = true;
    try {
      const testDiv = document.createElement('div');
      testDiv.id = 'mutation-test';
      document.body.appendChild(testDiv);
      document.body.removeChild(testDiv);
    } catch {
      results.domMutationAllowed = false;
    }

    // Test 8: Check for Content Security Policy
    results.cspHeaders = document.querySelector('meta[http-equiv="Content-Security-Policy"]') !== null;

    setTestResults(results);
  };

  const TestResult = ({ label, value, description }: { label: string; value: any; description: string }) => {
    const getIcon = () => {
      if (typeof value === 'boolean') {
        return value ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />;
      }
      return <Info className="w-5 h-5 text-blue-500" />;
    };

    return (
      <div className="flex items-start gap-3 p-4 bg-white rounded-lg border">
        {getIcon()}
        <div className="flex-1">
          <div className="font-semibold">{label}</div>
          <div className="text-sm text-gray-600">{description}</div>
          <div className="text-xs text-gray-500 mt-1">Result: {String(value)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Modal & Pop-up Blocker Test</h1>

        {/* Browser Info Section */}
        <div className="bg-white rounded-lg p-6 mb-8 border">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Browser Information
          </h2>
          <div className="space-y-2 text-sm">
            <div><strong>Platform:</strong> {browserInfo.platform}</div>
            <div><strong>Language:</strong> {browserInfo.language}</div>
            <div><strong>Cookies Enabled:</strong> {String(browserInfo.cookiesEnabled)}</div>
            <div><strong>Do Not Track:</strong> {browserInfo.doNotTrack || 'Not set'}</div>
            <div className={`font-semibold ${browserInfo.possibleAdBlocker ? 'text-red-600' : 'text-green-600'}`}>
              <strong>Ad Blocker Detected:</strong> {browserInfo.possibleAdBlocker ? 'Yes (Possible)' : 'No'}
            </div>
          </div>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-lg p-6 mb-8 border">
          <h2 className="text-xl font-semibold mb-4">Test Controls</h2>
          <div className="flex gap-4">
            <Button 
              onClick={() => {
                setShowModal(true);
                setTimeout(runTests, 100);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Open Test Modal & Run Tests
            </Button>
            <Button 
              onClick={runTests}
              variant="outline"
            >
              Run Tests Only
            </Button>
          </div>
        </div>

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <div className="bg-white rounded-lg p-6 mb-8 border">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Test Results
            </h2>
            <div className="space-y-3">
              <TestResult 
                label="Modal in DOM" 
                value={testResults.modalInDOM}
                description="Checks if the modal dialog element is present in the DOM"
              />
              <TestResult 
                label="Portal Container Exists" 
                value={testResults.portalExists}
                description="Checks if Radix UI portal container is created"
              />
              <TestResult 
                label="Overlay Exists" 
                value={testResults.overlayExists}
                description="Checks if the modal overlay backdrop is rendered"
              />
              <TestResult 
                label="Highest Z-Index" 
                value={testResults.highestZIndex}
                description="Shows the highest z-index on the page (should be 50+ for modal)"
              />
              <TestResult 
                label="LocalStorage Works" 
                value={testResults.localStorageWorks}
                description="Tests if localStorage is accessible (needed for form data)"
              />
              <TestResult 
                label="Pop-up Blocker Active" 
                value={testResults.popupBlockerActive}
                description="Tests if traditional pop-up windows are blocked"
              />
              <TestResult 
                label="DOM Mutation Allowed" 
                value={testResults.domMutationAllowed}
                description="Tests if JavaScript can modify the DOM"
              />
              <TestResult 
                label="CSP Headers Present" 
                value={testResults.cspHeaders}
                description="Checks for Content Security Policy restrictions"
              />
            </div>
          </div>
        )}

        {/* Diagnostic Summary */}
        {Object.keys(testResults).length > 0 && (
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h2 className="text-xl font-semibold mb-4">Diagnostic Summary</h2>
            <div className="space-y-2">
              {!testResults.modalInDOM && showModal && (
                <div className="text-red-600 font-semibold">
                  ⚠️ Modal is not appearing in DOM - likely being blocked!
                </div>
              )}
              {testResults.popupBlockerActive && (
                <div className="text-yellow-600">
                  ℹ️ Pop-up blocker is active (but shouldn't affect overlay modals)
                </div>
              )}
              {browserInfo.possibleAdBlocker && (
                <div className="text-yellow-600">
                  ℹ️ Ad blocker detected - may interfere with modal rendering
                </div>
              )}
              {!testResults.localStorageWorks && (
                <div className="text-red-600">
                  ⚠️ localStorage is blocked - form data cannot be saved
                </div>
              )}
              {testResults.modalInDOM && showModal && (
                <div className="text-green-600 font-semibold">
                  ✅ Modal is rendering correctly!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Test Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Test Modal</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-green-600 font-semibold mb-4">
                ✅ If you can see this, the modal is working!
              </p>
              <p className="text-sm text-gray-600">
                This is a test modal using the same Dialog component as the onboarding form.
                If this modal appears, but the onboarding modal doesn't, there may be a code issue
                rather than a blocker issue.
              </p>
              <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
                <strong>Debug Info:</strong><br/>
                Role: {document.querySelector('[role="dialog"]') ? 'dialog found' : 'not found'}<br/>
                Z-Index: Check developer tools
              </div>
            </div>
            <Button onClick={() => setShowModal(false)}>Close Test Modal</Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ModalTest;